import { useEffect, useState, type ReactNode } from "react";
import { ArrowLeft, Check, Loader2, MapPin } from "lucide-react";
import { ROUNDED_DISPLAY } from "../PintoAuthShell";
import { useTranslation } from "../../context/LanguageContext";
import { getStoreActivity } from "../../services/StoreActivityService";

/**
 * Shared pieces of the web onboarding (ZIP → plan → stores → checkout →
 * "You're in" → "Where else do you shop?"), styled after pinto's light theme.
 */

// Theme-aware (src/index.css --onbp-*): the original light colours, neutral in dark
// mode. Pages that still call useEnforceLightMode keep the light values.
export const ONB_INK = "var(--onbp-ink)";
export const ONB_MUTED = "var(--onbp-muted)";
export const ONB_TEXT2 = "var(--onbp-text2)";
export const ONB_BG = "var(--onbp-bg)";
export const ONB_BORDER = "var(--onbp-border)";
export const ONB_CTA = "#7C3AED";
export const ONB_SEL_TINT = "var(--onbp-sel-tint)";

export interface OnboardingStore {
  normalizedId: string;
  normalizedAddress: string;
  distance?: number;
}

/** Full-height light screen with a centered phone-width column. */
export function OnboardingScreen({
  children,
  background = ONB_BG,
  className = "",
  testId,
}: {
  children: ReactNode;
  background?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={`flex min-h-[100svh] w-full justify-center ${ROUNDED_DISPLAY}`}
      style={{ backgroundColor: background, color: ONB_INK }}
    >
      <div className={`flex min-h-[100svh] w-full max-w-[480px] flex-col ${className}`}>{children}</div>
    </div>
  );
}

export function TopBar({ left, center, right }: { left?: ReactNode; center?: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex min-h-[52px] items-center justify-between gap-2 px-3 pt-2">
      <div className="flex min-w-[44px] justify-start">{left}</div>
      <div className="flex min-w-0 flex-1 justify-center">{center}</div>
      <div className="flex min-w-[44px] justify-end">{right}</div>
    </div>
  );
}

export function BackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-11 w-11 items-center justify-center rounded-full transition hover:bg-black/5 dark:hover:bg-white/10"
      style={{ color: ONB_MUTED }}
    >
      <ArrowLeft size={22} />
    </button>
  );
}

export function PrimaryCta({
  children,
  onClick,
  disabled,
  busy,
  ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled || busy}
      className="flex h-[58px] w-full items-center justify-center gap-2.5 rounded-full text-[18px] font-black text-white transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
      style={{ backgroundColor: ONB_CTA, boxShadow: busy || !disabled ? "0 10px 24px rgba(124,58,237,0.35)" : undefined }}
    >
      {busy ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="sr-only">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

export function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-[14px] border border-red-600/15 bg-red-50 px-3 py-3 text-center text-[13px] font-semibold text-red-700 dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-300">
      {error}
    </div>
  );
}

/** "1101 S Military Trl, Deerfield Beach, FL 33442" → { street, city }. */
function splitAddress(address: string): { street: string; city: string } {
  const [street = "", city = ""] = address.split(",").map((part) => part.trim());
  return { street, city };
}

/**
 * Tap-to-check store list (pinto ProductsStoreOnboardingModal): nearest first,
 * up to `limit`, each row showing "Deals now" / "Deals within 24h" once known.
 */
export function StoreChecklist({
  retailer,
  stores,
  selected,
  limit,
  onChange,
  zip,
  onChangeZip,
  emptyText,
}: {
  retailer: string;
  /** null while loading */
  stores: OnboardingStore[] | null;
  selected: string[];
  limit: number;
  onChange: (next: string[]) => void;
  zip?: string;
  onChangeZip?: () => void;
  emptyText: string;
}) {
  const { t } = useTranslation();
  const [activity, setActivity] = useState<Record<string, boolean>>({});
  const idsKey = (stores ?? []).map((store) => store.normalizedId).join(",");

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    getStoreActivity(retailer, idsKey.split(","))
      .then((result) => {
        if (!cancelled && result) setActivity(result);
      })
      .catch(() => {
        // Status is a hint; the list works without it.
      });
    return () => {
      cancelled = true;
    };
  }, [retailer, idsKey]);

  const full = selected.length >= limit;
  const toggle = (id: string) => {
    if (selected.includes(id)) onChange(selected.filter((value) => value !== id));
    else if (!full) onChange([...selected, id]);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-1 pb-2.5 pt-1">
        {zip !== undefined ? (
          <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: ONB_MUTED }}>
            <MapPin size={15} />
            {t("onboarding2.near", { zip: zip || "—" })}
            {onChangeZip ? (
              <>
                {" · "}
                <button type="button" onClick={onChangeZip} className="font-semibold hover:underline" style={{ color: ONB_CTA }}>
                  {t("onboarding2.change")}
                </button>
              </>
            ) : null}
          </span>
        ) : (
          <span />
        )}
        <span className="text-sm font-bold" style={{ color: ONB_CTA }}>
          {t("onboarding2.count", { count: selected.length, limit })}
        </span>
      </div>

      <div className="min-h-[160px] flex-1 overflow-y-auto rounded-2xl border" style={{ borderColor: "var(--onbp-row-border)", backgroundColor: "var(--onbp-surface)" }}>
        {stores === null ? (
          <div className="flex justify-center py-10" aria-label="Loading">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: ONB_CTA }} />
          </div>
        ) : stores.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm font-semibold" style={{ color: ONB_MUTED }}>
            {emptyText}
          </p>
        ) : (
          <ul>
            {stores.map((store, index) => {
              const id = store.normalizedId;
              const on = selected.includes(id);
              const dim = !on && full;
              const { street, city } = splitAddress(store.normalizedAddress);
              const known = Object.prototype.hasOwnProperty.call(activity, id);
              const color = activity[id] ? "#059669" : "#D97706";
              return (
                <li key={id} className={index > 0 ? "border-t" : ""} style={{ borderColor: "var(--onbp-row-border)" }}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    aria-disabled={dim}
                    aria-label={`${city || street}, #${id}`}
                    onClick={() => toggle(id)}
                    className={`flex w-full items-center gap-3 px-3.5 py-[13px] text-left transition-colors ${dim ? "opacity-45" : "hover:bg-[var(--onbp-row-hover)]"}`}
                    style={on ? { backgroundColor: "var(--onbp-row-selected)" } : undefined}
                  >
                    <span
                      className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2"
                      style={on ? { backgroundColor: ONB_CTA, borderColor: ONB_CTA } : { borderColor: "var(--onbp-radio)" }}
                    >
                      {on ? <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base font-bold" style={{ color: ONB_INK }}>
                        {city || street}
                      </span>
                      <span className="block truncate text-[13px]" style={{ color: ONB_MUTED }}>
                        #{id}
                        {city ? ` · ${street}` : ""}
                      </span>
                      {known ? (
                        <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color }}>
                          <span className="h-[7px] w-[7px] rounded-full" style={{ backgroundColor: color }} />
                          {activity[id] ? t("onboarding2.deals_now") : t("onboarding2.deals_within_24h")}
                        </span>
                      ) : null}
                    </span>
                    {store.distance !== undefined ? (
                      <span className="flex-shrink-0 text-sm font-bold" style={{ color: ONB_INK }}>
                        {t("onboarding2.miles", { miles: store.distance.toFixed(1) })}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
